import {
  useMemo,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
} from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-enterprise'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { CellClassParams } from 'ag-grid-community'
import { defaultColDef } from '../gridConfig/DefaultColDef'
import { AG_GRID_LOCALE_SV } from '../../../MainGrids/gridConfig/SwedishLocale'
import TotalTextCellRenderer from '../cellRenderers/TotalTextCellRenderer'
import LoadingSpinner from '../../../StatusIndicators/LoadingSpinner'
import { customAggFuncs } from '../helper/customAggFuncs'
import { LicenseManager } from 'ag-grid-enterprise'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'
import { useProjektprognosInteractionContext } from '../../context/ProjektprognosInteractionContext'
import { useProjektprognosSheetContext } from '../../context/ProjektprognosSheetContext'
import { useProjektprognosPerformanceNotices } from '../../fetchData/queries/performance/usePerformanceNotices'
import { usePerformanceReportData } from '../../fetchData/queries/performance/usePerformanceReportData'
import { UnableToLoadData } from '../../../SystemState/ErrorManagement/UnableToLoadData/UnableToLoadData'
import { useStatusBar } from '../customHooks/useStatusBar'
import { useGetRowId } from '../customHooks/useGetRowId'
import { useOnCellDoubleClicked } from '../customHooks/useOnCellDoubleClicked'
import { useOnCellContextMenu } from '../customHooks/useOnCellContextMenu'
import { useGetContextMenuItems } from '../customHooks/useGetContextMenuItems'
import { gridLicenseKey } from '../gridLicenseKey'
import { usePerformanceColDefs } from './useProjektprognosPerformanceColDefs'
import { extractAccountsFromData } from '../AccountFilter/utils/extractAccounts'
import { useExternalAccountFilter } from '../AccountFilter/hooks/useExternalAccountFilter'
import { createGetRowClassCallback } from '../helper/filterFunctions'
import { applyCommentsToGridRows } from '../../fetchData/helpers/applyCommentsToGridRows'
import { useCommentUpdater } from '../customHooks/useCommentUpdater'

LicenseManager.setLicenseKey(gridLicenseKey)

interface PerformanceGridProps {
  setIsAccountSpecificationModalOpen: Dispatch<SetStateAction<boolean>>
  modalAccountId: string
  setModalAccountId: Dispatch<SetStateAction<string>>
  modalOutcomeDate: string
  setModalOutcomeDate: Dispatch<SetStateAction<string>>
}

const PerformanceGrid = ({
  setIsAccountSpecificationModalOpen,
  modalAccountId,
  setModalAccountId,
  modalOutcomeDate,
  setModalOutcomeDate,
}: PerformanceGridProps) => {
  const {
    rows: reportRows,
    totals: reportTotals,
    previousSnapshotName,
    isLoading: isReportLoading,
    error: reportError,
  } = usePerformanceReportData()
  const accountNotices = useProjektprognosPerformanceNotices().data

  const {
    performanceGridRef,
    lockedBudget,
    performanceRows,
    setPerformanceRows,
    performanceTotals,
    setPerformanceTotals,
    selectedPerformanceAccountIds,
    filterActive,
    minDiff,
    enableDiffFilter,
    enableCommentFilter,
  } = useProjektprognosSheetContext()

  const { loadProjektprognos } = useProjektprognosInteractionContext()

  const { emptyRowsHiddenToggle, selectedPeriodReport } =
    useProjektprognosEditingContext()

  // Show loading overlay when report data is loading
  useEffect(() => {
    if (performanceGridRef.current?.api) {
      if (isReportLoading) {
        performanceGridRef.current.api.showLoadingOverlay()
      } else {
        performanceGridRef.current.api.hideOverlay()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReportLoading])

  // Set rows from report data (snapshot or live)
  useEffect(() => {
    if (reportRows) setPerformanceRows(reportRows)
  }, [loadProjektprognos, reportRows, setPerformanceRows])

  // Set totals from report data (snapshot or live)
  useEffect(() => {
    if (reportTotals) {
      setPerformanceTotals(reportTotals)
    }
  }, [reportTotals, setPerformanceTotals])

  // Apply comments from period report to rows
  useEffect(() => {
    if (!reportRows) return

    // Clear all old comments from rows (from previous comment system)
    const clearComments = (rows: any) => {
      return {
        ...rows,
        prefixedData: rows.prefixedData.map((row: any) => ({
          ...row,
          comment: undefined,
        })),
        emptyRowsHidden: rows.emptyRowsHidden.map((row: any) => ({
          ...row,
          comment: undefined,
        })),
      }
    }

    if (
      selectedPeriodReport &&
      selectedPeriodReport.comments?.accountComments &&
      selectedPeriodReport.comments.accountComments.length > 0
    ) {
      const clearedRows = clearComments(reportRows)
      const updatedRows = applyCommentsToGridRows(
        clearedRows,
        selectedPeriodReport.comments.accountComments
      )
      if (updatedRows) {
        setPerformanceRows(updatedRows)
      }
    } else {
      // No period report selected - clear all comments
      const clearedRows = clearComments(reportRows)
      setPerformanceRows(clearedRows)
    }
  }, [selectedPeriodReport, reportRows, setPerformanceRows])

  const effectiveEnableDiffFilter = enableDiffFilter && !isReportLoading

  // Extract unique accounts for filtering
  const accounts = useMemo(() => {
    return extractAccountsFromData(performanceRows?.prefixedData)
  }, [performanceRows?.prefixedData])

  const allAccountIds = useMemo<Set<number>>(() => {
    return new Set(accounts.map((a) => a.accountId))
  }, [accounts])

  // Use external filter system to show group rows even when all children are filtered out
  const { isExternalFilterPresent, doesExternalFilterPass } =
    useExternalAccountFilter(
      selectedPerformanceAccountIds,
      allAccountIds,
      filterActive,
      accounts,
      effectiveEnableDiffFilter,
      minDiff,
      enableCommentFilter
    )

  // Update AG Grid filter when filterActive or selectedAccountIds change
  useEffect(() => {
    if (performanceGridRef.current?.api) {
      performanceGridRef.current.api.onFilterChanged()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterActive,
    selectedPerformanceAccountIds,
    effectiveEnableDiffFilter,
    enableCommentFilter,
    minDiff,
    doesExternalFilterPass,
  ])

  // Mark selected rows with focusAccount CSS class and accounts with high diff with red background
  const getRowClass = useMemo(
    () =>
      createGetRowClassCallback(
        selectedPerformanceAccountIds,
        accounts,
        minDiff,
        enableCommentFilter
      ),
    [selectedPerformanceAccountIds, accounts, minDiff, enableCommentFilter]
  )

  const performanceColDefs = usePerformanceColDefs({
    lockedBudget,
    performanceTotals,
    selectedSnapshotName: previousSnapshotName,
  })
  const getRowId = useGetRowId()
  const statusBar = useStatusBar()
  const onCellContextMenu = useOnCellContextMenu(
    modalAccountId,
    modalOutcomeDate
  )

  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), [])

  // Check if period report is active and not closed (editable)
  const isPeriodReportActive = !!selectedPeriodReport
  const isPeriodReportEditable =
    isPeriodReportActive && selectedPeriodReport?.status !== 'Closed'

  const { updateComment } = useCommentUpdater({
    selectedPeriodReportId: selectedPeriodReport?.id || null,
    isPeriodReportEditable,
  })

  // Combined cell value changed handler
  const handleCellValueChanged = useCallback(
    (event: any) => {
      if (isPeriodReportActive && event.colDef.field === 'comment') {
        const accountId = event.data?.accountId
        if (accountId) {
          updateComment(accountId, event.newValue || '')
        }
      }
    },
    [isPeriodReportActive, updateComment]
  )
  const onCellDoubleClicked = useOnCellDoubleClicked(performanceGridRef)

  const getContextMenuItemsHook = {
    totals: performanceTotals,
    setIsAccountSpecificationModalOpen,
    setModalAccountId,
    setModalOutcomeDate,
  }

  const getContextMenuItems = useGetContextMenuItems({
    ...getContextMenuItemsHook,
  })

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'Resultatuppföljning',
      cellRendererParams: {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        innerRenderer: TotalTextCellRenderer,
        innerRendererParams: { ...accountNotices },
      },
      field: 'displayName',
      flex: 2,
      cellStyle: (params: CellClassParams) => {
        const colStyle = { backgroundColor: '#d9dcde', fontWeight: '' }

        switch (params.node.level) {
          case 1:
            colStyle.backgroundColor = `${colStyle.backgroundColor}30`
            colStyle.fontWeight = '700'
            break
          case 2:
            colStyle.backgroundColor = params.node.data.editable
              ? 'transparent'
              : `${colStyle.backgroundColor}30`
            break
          default:
            colStyle.backgroundColor = `${colStyle.backgroundColor}80`
            colStyle.fontWeight = '900'
            break
        }
        return colStyle
      },
    }
  }, [accountNotices])

  return (
    <div
      className="ag-theme-balham"
      style={gridStyle}
      data-testid="PerformanceGrid"
    >
      {reportError ? (
        <UnableToLoadData message={'Resultatuppföljning kunde ej laddas'} />
      ) : (
        <AgGridReact
          ref={performanceGridRef}
          columnDefs={performanceColDefs}
          rowData={
            emptyRowsHiddenToggle
              ? performanceRows?.emptyRowsHidden
              : performanceRows?.prefixedData
          }
          rowSelection={'multiple'}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          getContextMenuItems={getContextMenuItems}
          onCellContextMenu={onCellContextMenu}
          onCellDoubleClicked={onCellDoubleClicked}
          onCellValueChanged={handleCellValueChanged}
          groupDefaultExpanded={1}
          statusBar={statusBar}
          enableRangeSelection={true}
          enableRangeHandle={true}
          localeText={AG_GRID_LOCALE_SV}
          loadingOverlayComponent={LoadingSpinner}
          stopEditingWhenCellsLoseFocus={true}
          suppressAggFuncInHeader={true}
          getRowId={getRowId}
          groupRowsSticky={true}
          aggFuncs={customAggFuncs}
          groupAllowUnbalanced
          groupMaintainOrder
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          suppressAggFilteredOnly={true}
          suppressRowHoverHighlight={true}
          getRowClass={getRowClass}
        />
      )}
    </div>
  )
}

export default PerformanceGrid
